import { forwardRef, type ButtonHTMLAttributes } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@shared/lib/cn';
import { Icon } from './Icon';
import { Spinner } from './Spinner';
import { Tooltip } from './Tooltip';

export type IconButtonVariant = 'ghost' | 'outline' | 'filled' | 'danger';

const VARIANTS: Record<IconButtonVariant, string> = {
  ghost: 'bg-transparent text-secondary hover:bg-surface-tint hover:text-primary',
  outline: 'border border-border bg-surface text-secondary hover:bg-surface-tint',
  filled: 'bg-primary text-on-primary hover:bg-primary-hover',
  danger: 'bg-transparent text-danger-text hover:bg-danger-subtle',
};

const SIZES = { sm: 'size-8', md: 'size-10', lg: 'size-12' } as const;

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label' | 'children'> {
  icon: LucideIcon;
  /** Required accessible name, also shown as tooltip (design system §4.2). */
  label: string;
  variant?: IconButtonVariant;
  size?: keyof typeof SIZES;
  loading?: boolean;
  /** Toggle buttons expose aria-pressed and a tinted background when on. */
  pressed?: boolean;
  showTooltip?: boolean;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon, label, variant = 'ghost', size = 'md', loading, pressed, showTooltip = true, className, type, disabled, ...rest },
  ref,
) {
  const button = (
    <button
      ref={ref}
      type={type ?? 'button'}
      aria-label={label}
      aria-pressed={pressed}
      aria-busy={loading || undefined}
      disabled={disabled || loading}
      className={cn(
        'relative inline-flex shrink-0 items-center justify-center rounded-md transition-colors duration-micro',
        'disabled:cursor-not-allowed disabled:opacity-50 max-md:min-h-touch-min max-md:min-w-touch-min',
        VARIANTS[variant],
        SIZES[size],
        pressed && 'bg-primary-subtle text-on-primary-subtle',
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner size={20} /> : <Icon icon={icon} size={20} />}
    </button>
  );
  return showTooltip ? <Tooltip content={label}>{button}</Tooltip> : button;
});
